import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import ScrollReveal from '@/components/base/ScrollReveal';

const RESULTS_ITEMS = [
  { icon: 'ri-eye-2-line', key: 'results1', count: 85, suffix: '%' },
  { icon: 'ri-user-add-line', key: 'results2', count: 3, suffix: 'x' },
  { icon: 'ri-stack-line', key: 'results3', count: 100, suffix: '%' },
  { icon: 'ri-folder-chart-line', key: 'results4', count: 90, suffix: '%' },
  { icon: 'ri-database-2-line', key: 'results5', count: 2, suffix: 'x' },
  { icon: 'ri-arrow-up-circle-line', key: 'results6', count: 4, suffix: 'x' },
];

function CounterBadge({ count, suffix, isVisible }: { count: number; suffix: string; isVisible: boolean }) {
  const [current, setCurrent] = useState(0);

  useEffect(() => {
    if (!isVisible) return;
    const duration = 1800;
    const steps = 60;
    const increment = count / steps;
    let step = 0;
    const timer = setInterval(() => {
      step++;
      setCurrent(Math.min(Math.round(increment * step), count));
      if (step >= steps) clearInterval(timer);
    }, duration / steps);
    return () => clearInterval(timer);
  }, [isVisible, count]);

  return (
    <span className="font-heading text-2xl font-bold text-primary-500">
      {current}{suffix}
    </span>
  );
}

function ResultCard({ item, index }: { item: typeof RESULTS_ITEMS[0]; index: number }) {
  const { t } = useTranslation();
  const ref = useRef<HTMLDivElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(node);
        }
      },
      { threshold: 0.3 }
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  return (
    <div ref={ref} className="group bg-white border border-gray-100 rounded-2xl p-7 hover:border-primary-200 hover:shadow-[0_8px_32px_rgba(0,0,0,0.05)] hover:-translate-y-1 transition-all duration-400 text-center">
      <div className="w-14 h-14 rounded-2xl bg-primary-50 flex items-center justify-center mx-auto mb-4 group-hover:bg-primary-100 transition-colors duration-300">
        <i className={`${item.icon} text-primary-500 text-2xl`}></i>
      </div>
      <CounterBadge count={item.count} suffix={item.suffix} isVisible={isVisible} />
      <p className="text-foreground-600 text-sm mt-2">{t(`trafficPro.${item.key}`)}</p>
    </div>
  );
}

export default function ExpectedResults() {
  const { t } = useTranslation();

  return (
    <section className="py-20 md:py-28 bg-[#F7F8FA]">
      <div className="max-w-6xl mx-auto px-6 md:px-10">
        <ScrollReveal>
          <div className="text-center mb-16">
            <span className="inline-block px-4 py-1.5 rounded-full bg-primary-50 text-primary-700 text-xs font-semibold tracking-wider uppercase mb-4">
              {t('trafficPro.resultsTag')}
            </span>
            <h2 className="font-heading text-3xl md:text-5xl font-bold text-foreground-950">
              {t('trafficPro.resultsTitle')}{' '}
              <span className="text-primary-500">{t('trafficPro.resultsTitleHighlight')}</span>
            </h2>
          </div>
        </ScrollReveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {RESULTS_ITEMS.map((item, i) => (
            <ResultCard key={item.key} item={item} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
}